import { useState } from 'react';

import { LogActions } from '../components/LogActions.js';
import { LogList } from '../components/LogList.js';
import { isHttpUrl } from '../core/format.js';
import { DEFAULT_STATUS_ENDPOINT, useSettings } from '../state/SettingsProvider.js';
import type { AppMode } from '../core/types.js';

export function SettingsPage() {
  const { mode, piUrl, statusEndpoint, setMode, setPiUrl, setStatusEndpoint } =
    useSettings();
  const [draftUrl, setDraftUrl] = useState(piUrl);
  const [draftStatus, setDraftStatus] = useState(statusEndpoint);
  const [savedPi, setSavedPi] = useState(false);
  const [savedStatus, setSavedStatus] = useState(false);

  const onModeChange = (next: AppMode) => {
    setMode(next);
  };

  const onSaveUrl = () => {
    const trimmed = draftUrl.trim();
    if (!isHttpUrl(trimmed)) return;
    setPiUrl(trimmed);
    setSavedPi(true);
    window.setTimeout(() => setSavedPi(false), 1500);
  };

  const onSaveStatus = () => {
    const trimmed = draftStatus.trim();
    if (!isHttpUrl(trimmed)) return;
    setStatusEndpoint(trimmed);
    setSavedStatus(true);
    window.setTimeout(() => setSavedStatus(false), 1500);
  };

  return (
    <main className="main">
      <section className="section">
        <h2>Mode</h2>
        <p className="muted">
          <strong>Device</strong> mode is the field-tester surface — connect to
          a Soluu, configure a real WiFi network, and report what happened.{' '}
          <strong>Device + AP</strong> mode adds the bench-test surface that
          drives a Pi running pi-ap-control.
        </p>
        <div className="toggle-group" role="group" aria-label="Mode">
          <button
            type="button"
            className={mode === 'device' ? 'active' : ''}
            onClick={() => onModeChange('device')}
          >
            Device
          </button>
          <button
            type="button"
            className={mode === 'device+ap' ? 'active' : ''}
            onClick={() => onModeChange('device+ap')}
          >
            Device + AP
          </button>
        </div>
      </section>

      <section className="section">
        <h2>Pi Backend URL</h2>
        <p className="muted">
          Where pi-ap-control is reachable on the LAN. Defaults to{' '}
          <code>http://synclet.local:3000</code>. Only used in Device + AP mode.
        </p>
        <div className="field">
          <label htmlFor="pi-url">URL</label>
          <input
            id="pi-url"
            type="url"
            value={draftUrl}
            onChange={(e) => setDraftUrl(e.target.value)}
            placeholder="http://synclet.local:3000"
          />
        </div>
        <div className="row">
          <button
            type="button"
            className="primary"
            onClick={onSaveUrl}
            disabled={!isHttpUrl(draftUrl.trim()) || draftUrl.trim() === piUrl}
          >
            {savedPi ? 'Saved!' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => {
              setDraftUrl('http://synclet.local:3000');
            }}
          >
            Reset to default
          </button>
        </div>
        {!isHttpUrl(draftUrl.trim()) && draftUrl.trim().length > 0 && (
          <p className="muted">URL must start with http:// or https://.</p>
        )}
      </section>

      <section className="section">
        <h2>Soluu Status Endpoint</h2>
        <p className="muted">
          URL the Soluu device pings to confirm WAN reachability. Without this,
          the device stalls at <code>CONNECTED</code> and never advances to{' '}
          <code>ON_INTERNET</code>. Default points at the Soluu production{' '}
          <code>/status</code> endpoint. The scheme prefix is stripped before
          the URL is written to the device.
        </p>
        <div className="field">
          <label htmlFor="status-url">URL</label>
          <input
            id="status-url"
            type="url"
            value={draftStatus}
            onChange={(e) => setDraftStatus(e.target.value)}
            placeholder={DEFAULT_STATUS_ENDPOINT}
          />
        </div>
        <div className="row">
          <button
            type="button"
            className="primary"
            onClick={onSaveStatus}
            disabled={
              !isHttpUrl(draftStatus.trim()) || draftStatus.trim() === statusEndpoint
            }
          >
            {savedStatus ? 'Saved!' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => {
              setDraftStatus(DEFAULT_STATUS_ENDPOINT);
            }}
          >
            Reset to default
          </button>
        </div>
        {!isHttpUrl(draftStatus.trim()) && draftStatus.trim().length > 0 && (
          <p className="muted">URL must start with http:// or https://.</p>
        )}
      </section>

      <section className="section">
        <h2>Session Log</h2>
        <LogList />
        <LogActions />
      </section>
    </main>
  );
}
